import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiCheck,
  mdiChevronRight,
  mdiInformationOutline
} from '@mdi/js'
import Icon from '@mdi/react'
import {Alert, Box, Snackbar, Typography} from '@mui/material'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

/**
 * Generic field button component.
 * Pure presentation component - receives all data as props.
 * Replaces the old StepButton with hardcoded logic.
 *
 * Used in overview screens to display field values and navigate to editors.
 * Supports multiline values via whiteSpace: pre-line.
 */
export interface FieldButtonProps {
  /** Field label (e.g., "Quantity", "Material") */
  label: string
  /** Formatted display value - null shows "Not specified". Supports newlines. */
  value: string | null
  /** Click handler for navigation */
  onClick: () => void
  /** Optional hint text */
  hint?: string
  /** Optional error text */
  error?: string
  /** Visual state */
  state?: 'success' | 'error' | 'warning'
  /** Whether this field requires dispatcher approval */
  requiresApproval?: boolean
  /** Whether the field is read-only */
  isReadOnly?: boolean
  /** Whether the field value has been changed */
  isChanged?: boolean
  /** Test ID for automation */
  'data-test-id'?: string
}

// State-based styling
// TODO: Unify all state-based styling (error/warning/success) into a single source of truth, maybe via theme or a helper function
const getStateStyles = (displayState?: 'success' | 'error' | 'warning') => {
  switch (displayState) {
    case 'error':
      return {borderColor: '#da0901', iconColor: '#da0901', hintColor: '#da0901'}
    case 'warning':
      return {borderColor: '#f5a623', iconColor: '#f5a623', hintColor: '#52667a'}
    case 'success':
      return {borderColor: '#00874a', iconColor: '#00874a', hintColor: '#52667a'}
    default:
      return {borderColor: '#e8ecf0', iconColor: '#52667a', hintColor: '#52667a'}
  }
}

/**
 * Generic field button for order editor overview.
 * Click to navigate to field editor.
 * Uses MUI components with multiline support.
 */
export const FieldButton: React.FC<FieldButtonProps> = ({
  label,
  value,
  onClick,
  hint,
  error,
  requiresApproval,
  state,
  isReadOnly,
  isChanged,
  'data-test-id': testId
}) => {
  const {t} = useTranslation()
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  // Handle null value - show "Not specified"
  const displayValue = value ?? t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')

  // Override state to error/warning
  const displayState = error
    ? 'error'
    : requiresApproval
      ? 'warning'
      : isChanged
        ? 'success'
        : state

  const styles = getStateStyles(displayState)

  // Icon mapping by state
  const stateIconMap: Record<string, string> = {
    warning: mdiAlertOutline,
    error: mdiAlertCircleOutline,
    success: mdiCheck
  }
  const icon = isReadOnly
    ? mdiInformationOutline
    : (displayState && stateIconMap[displayState]) || mdiChevronRight

  // Override hint if approval is required
  const displayHint = error
    ? error
    : requiresApproval
      ? t('orderIntake.rmc.orderManagement.requiresDispatchApproval', 'Requires dispatch approval')
      : hint

  const onInfoClick = () => {
    setSnackbarOpen(true)
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  const handleClick = () => {
    if (isReadOnly) {
      onInfoClick()
    } else {
      onClick()
    }
  }

  return (
    <>
      <Box
        onClick={handleClick}
        data-test-id={testId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '12px',
          py: '4px',
          borderRadius: '6px',
          border: isReadOnly ? 'none' : `1px solid ${styles.borderColor}`,
          backgroundColor: '#ffffff',
          cursor: isReadOnly ? 'default' : 'pointer',
          transition: 'border-color 0.2s',
          minHeight: '48px',
          '&:hover': {
            borderColor: isReadOnly ? 'transparent' : displayState ? styles.borderColor : '#b0bac5'
          }
        }}
      >
        <Box sx={{flex: 1, minWidth: 0}}>
          {/* Label */}
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '16px',
              color: '#52667a',
              fontFamily: 'Inter'
            }}
          >
            {label}
          </Typography>

          {/* Value - supports multiline via whiteSpace: pre-line */}
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '22px',
              color: '#29333d',
              fontFamily: 'Inter',
              whiteSpace: 'pre-line',
              wordBreak: 'break-word'
            }}
          >
            {displayValue}
          </Typography>

          {/* Hint */}
          {displayHint && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
                color: styles.hintColor,
                fontFamily: 'Inter',
                mt: '2px'
              }}
            >
              {displayHint}
            </Typography>
          )}
        </Box>

        {/* Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 1,
            color: styles.iconColor
          }}
        >
          <Icon path={icon} size={0.75} />
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert onClose={handleSnackbarClose} severity="info" sx={{width: '100%'}}>
          {t(
            'orderIntake.rmc.orderManagement.fieldReadOnly',
            'Editing is currently not possible. Please contact dispatch for changes.'
          )}
        </Alert>
      </Snackbar>
    </>
  )
}
